import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

/** A node in a folder template tree (as returned/sent by the API). */
export interface FolderTemplateTreeNode {
  id?: string;
  name: string;
  isDefault: boolean;
  children: FolderTemplateTreeNode[];
}

export interface FolderTemplate {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FolderTemplateWithNodes extends FolderTemplate {
  nodes: FolderTemplateTreeNode[];
}

/** Node payload sent on create/update. */
export interface FolderTemplateNodePayload {
  name: string;
  is_default?: boolean;
  children?: FolderTemplateNodePayload[];
}

export interface CreateFolderTemplatePayload {
  name: string;
  description?: string | null;
  is_default?: boolean;
  is_active?: boolean;
  nodes?: FolderTemplateNodePayload[];
}

export interface UpdateFolderTemplatePayload {
  name?: string;
  description?: string | null;
  is_default?: boolean;
  is_active?: boolean;
  nodes?: FolderTemplateNodePayload[];
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const folderTemplateKeys = {
  all: ['folder-templates'] as const,
  list: () => [...folderTemplateKeys.all, 'list'] as const,
  detail: (id: string) => [...folderTemplateKeys.all, 'detail', id] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/** Lists folder templates for the tenant. */
export function useFolderTemplates(options?: { includeInactive?: boolean }) {
  return useQuery({
    queryKey: folderTemplateKeys.list(),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (options?.includeInactive) params.include_inactive = 'true';

      const response = await apiClient.get<ApiResponse<Array<Record<string, unknown>>>>(
        '/folder-templates',
        { params },
      );
      return (response.data.data ?? []).map(mapTemplate);
    },
  });
}

/** Fetches a single template with its node tree. */
export function useFolderTemplate(templateId: string) {
  return useQuery({
    queryKey: folderTemplateKeys.detail(templateId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/folder-templates/${templateId}`,
      );
      return mapTemplateWithNodes(response.data.data);
    },
    enabled: !!templateId,
  });
}

export function useCreateFolderTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateFolderTemplatePayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/folder-templates',
        payload,
      );
      return mapTemplateWithNodes(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderTemplateKeys.list() });
    },
  });
}

export function useUpdateFolderTemplate(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateFolderTemplatePayload) => {
      const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
        `/folder-templates/${templateId}`,
        payload,
      );
      return mapTemplateWithNodes(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderTemplateKeys.list() });
      queryClient.invalidateQueries({ queryKey: folderTemplateKeys.detail(templateId) });
    },
  });
}

export function useDeleteFolderTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      await apiClient.delete(`/folder-templates/${templateId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderTemplateKeys.list() });
    },
  });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function mapTemplate(raw: Record<string, unknown>): FolderTemplate {
  return {
    id: raw.id as string,
    name: (raw.name as string) ?? '',
    description: (raw.description as string) ?? null,
    isDefault: (raw.is_default as boolean) ?? (raw.isDefault as boolean) ?? false,
    isActive: (raw.is_active as boolean) ?? (raw.isActive as boolean) ?? true,
    createdAt: (raw.created_at as string) ?? (raw.createdAt as string) ?? '',
    updatedAt: (raw.updated_at as string) ?? (raw.updatedAt as string) ?? '',
  };
}

function mapNode(raw: Record<string, unknown>): FolderTemplateTreeNode {
  return {
    id: raw.id as string | undefined,
    name: (raw.name as string) ?? '',
    isDefault: (raw.is_default as boolean) ?? (raw.isDefault as boolean) ?? false,
    children: Array.isArray(raw.children)
      ? (raw.children as Record<string, unknown>[]).map(mapNode)
      : [],
  };
}

function mapTemplateWithNodes(raw: Record<string, unknown>): FolderTemplateWithNodes {
  return {
    ...mapTemplate(raw),
    nodes: Array.isArray(raw.nodes) ? (raw.nodes as Record<string, unknown>[]).map(mapNode) : [],
  };
}
